import React, { useEffect, useRef, useState } from 'react'
import { HeroCopyrightPosition } from '../../../data/types'
import PrimaryButton from '../../utils/PrimaryButton'
import ResponsiveImage from '../../utils/ResponsiveImage'
import { headerHeight, heroPadding } from '../constants'
import useStyles from './HeroComponent.styles'
import { HeroComponentProps } from './HeroComponentProps'
import ScrollArrow from './ScrollArrow'

import cvodR13 from '../../../images/cvod/cvod-13.svg'
import cvodRR13 from '../../../images/cvod/cvod-R13.svg'
import cvodRP13 from '../../../images/cvod/cvod-RP13.svg'
import cvodRR15 from '../../../images/cvod/cvod-R15.svg'
import cvodRP15 from '../../../images/cvod/cvod-RP15.svg'
import cvodR16 from '../../../images/cvod/cvod-16.svg'
import cvodRR16 from '../../../images/cvod/cvod-R16.svg'
import cvodRP16 from '../../../images/cvod/cvod-RP16.svg'
import cvodR18 from '../../../images/cvod/cvod-18.svg'
import cvodRR18 from '../../../images/cvod/cvod-R18.svg'
import cvodRP18 from '../../../images/cvod/cvod-RP18.svg'
import cvodG from '../../../images/cvod/cvod-g.svg'
import cvodM from '../../../images/cvod/cvod-m.svg'
import cvodPG from '../../../images/cvod/cvod-pg.svg'

const getIcon = (value: string): string  => {
  switch (value) {
    case 'G':
      return cvodG
    case 'PG':
      return cvodPG
    case 'M':
      return cvodM
    case 'R13':
      return cvodR13
    case 'RP13':
      return cvodRP13
    case 'RR13':
      return cvodRR13
    case 'RP15':
      return cvodRP15
    case 'RR15':
      return cvodRR15
    case 'R16':
      return cvodR16
    case 'RP16':
      return cvodRP16
    case 'RR16':
      return cvodRR16
    case 'R18':
      return cvodR18
    case 'RP18':
      return cvodRP18
    case 'RR18':
      return cvodRR18
    default:
      return ''
  }
}

function copyrightPosition(position: HeroCopyrightPosition) {
  const styles: {
    top?: number
    bottom?: number
    right?: number
    left?: number
  } = {}
  if (
    position === HeroCopyrightPosition.BottomLeft ||
    position === HeroCopyrightPosition.BottomRight
  ) {
    styles.bottom = 0
  } else {
    styles.top = 0
  }

  if (
    position === HeroCopyrightPosition.BottomLeft ||
    position === HeroCopyrightPosition.TopLeft
  ) {
    styles.left = 0
  } else {
    styles.right = 0
  }
  return styles
}

const HeroComponent: React.FC<HeroComponentProps> = ({
  images = [],
  title,
  rating,
  genre,
  season,
  year,
  blurb,
  copyright,
  button
}: HeroComponentProps) => {
  const classes = useStyles()
  const [shouldDisplayDownArrow, setShouldDisplayDownArrow] = useState(false)
  const imageRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const windowHeight = window.innerHeight
    const elementHeight = imageRef.current?.getBoundingClientRect().height
    if (elementHeight) {
      setShouldDisplayDownArrow(
        elementHeight + headerHeight + heroPadding > windowHeight
      )
    }
  })

  let image
  let mobileImage
  if (images && images.length) {
    [{ image, mobileImage }] = images
  }

  const renderGenre = () => {
    // Example of genre: 'Drama, Triller'
    if (!genre) return null

    // split & trim spaces
    const genreList = genre.split(",").map(item => item.trim()).filter(Boolean)

    // Remove duplicates
    const uniqueGenres = Array.from(new Set(genreList))

    return uniqueGenres.map(item => (
        <div className={classes.badge} key={item}>
          {item}
        </div>
      )
    )
  }
  
  return (
    <div className={classes.hero}>
      {image && mobileImage && (<ResponsiveImage
        ref={imageRef}
        src={image.src}
        mobileSrc={mobileImage.src}
        className={classes.image}
      >
        {copyright && (
          <div
            className={classes.copyright}
            style={{
              color: copyright.color ?? 'white',
              ...copyrightPosition(copyright.position)
            }}
          >
            {copyright.text}
          </div>
        )}
        {shouldDisplayDownArrow && <ScrollArrow />}
      </ResponsiveImage>
      )}

      <div className={classes.heroData}>
        <div className={classes.title}>{title}</div>
        
        <div className={classes.details}>
          <img
            src={getIcon(rating)}
            width='24px'
            alt='rating icon'
          />
         
          {renderGenre()}

          <div className={classes.badge}>{season}</div>
    
          <div className={classes.badge}>{year}</div>
        </div>

        <div className={classes.blurb}>{blurb}</div>

        {button && (
          <PrimaryButton
            href={button.href}
            isPrimary={button.isPrimary}
          >
            {button.text}
          </PrimaryButton>
        )}
      </div>
    </div>
  )
}
export default HeroComponent
